import { KitThreadCheckJob, KitThreadCheckKind } from 'lib/api/kitThreadCheck';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  GenericSuggestion,
  SuggestionPipelines,
} from 'types/suggestions/CoreSuggestions';

/**
 * Kit PostHog events summary:
 *
 * Selection / apply
 * - kit_suggestion_selected — Fired whenever `clickedSuggestion` changes.
 *
 * Engagement
 * - kit_category_toggled — Pipeline accordion expand/collapse.
 * - kit_info_hover — When user hovers Kit hints for more information.
 * - kit_suggestions_load_more — "Load more" / "Show N more".
 *
 * Titlebar pulse
 * - kit_view_pulse_shown — Pulse rendered on the opposite New/Active tab.
 * - Enrich existing switch_to_quote_view / switch_to_loads_view with had_pulse.
 *
 * Thread check ("Ask Kit to check this thread for")
 * - kit_thread_check_requested — Button `buttonNamePosthog` on a check chip (or Retry).
 * - kit_thread_check_completed — A check this rep watched finished, failed, or stopped polling.
 * - kit_thread_check_feedback — Thumbs up/down (and optional note) on a check's result.
 */

export enum KitPosthogEvents {
  SuggestionSelected = 'kit_suggestion_selected',
  CategoryToggled = 'kit_category_toggled',
  InfoHover = 'kit_info_hover',
  SuggestionsLoadMore = 'kit_suggestions_load_more',
  ViewPulseShown = 'kit_view_pulse_shown',
  ThreadCheckRequested = 'kit_thread_check_requested',
  ThreadCheckCompleted = 'kit_thread_check_completed',
  ThreadCheckFeedback = 'kit_thread_check_feedback',
  HandledQuoteSelectionChanged = 'kit_handled_quote_selection_changed',
  HandledQuoteBulkSelectionChanged = 'kit_handled_quote_bulk_selection_changed',
  HandledQuoteReset = 'kit_handled_quotes_reset',
  HandledQuoteViewToggled = 'kit_handled_quotes_view_toggled',
}

/**
 * How `clickedSuggestion` was set.
 * Distinguishes intentional engagement from auto-select and auto-advance.
 */
export enum KitSuggestionSelectionSource {
  UserClick = 'user_click',
  BatchApplyAll = 'batch_apply_all',
  AutoSelect = 'auto_select',
  AutoAdvance = 'auto_advance',
  GoToSuggestion = 'go_to_suggestion',
}

export enum KitPulseTargetView {
  Quote = 'quote',
  Loads = 'loads',
}

export enum KitCategoryToggleAction {
  Expand = 'expand',
  Collapse = 'collapse',
}

export enum KitInfoHoverTarget {
  NewTab = 'new_tab',
  ActiveTab = 'active_tab',
  SelectedKeyword = 'selected_keyword',
  HandledKeyword = 'handled_keyword',
  SuggestionPreview = 'suggestion_preview',
}

export enum KitHandledQuoteBulkSelectionAction {
  Select = 'select',
  Clear = 'clear',
}

export enum KitHandledQuoteView {
  Pending = 'pending',
  Previous = 'previous',
}

export type PosthogCapture = (
  event: string,
  properties?: Record<string, unknown>
) => void;

type PosthogClient = Maybe<{
  capture: PosthogCapture;
}>;

export function getPosthogCapture(
  posthog: PosthogClient
): Undef<PosthogCapture> {
  if (!posthog) {
    return undefined;
  }
  return (event, properties) => posthog.capture(event, properties);
}

type CaptureSuggestionSelectedArgs = {
  capture?: PosthogCapture;
  suggestion: GenericSuggestion;
  serviceID: Undef<number | string>;
  selectionSource: KitSuggestionSelectionSource;
  batchCount?: number;
};

// Fires `kit_suggestion_selected` when `clickedSuggestion` changes.
export function captureKitSuggestionSelected({
  capture,
  suggestion,
  serviceID,
  selectionSource,
  batchCount,
}: CaptureSuggestionSelectedArgs): void {
  if (!suggestion.pipeline || !capture) {
    return;
  }

  capture(KitPosthogEvents.SuggestionSelected, {
    pipeline: suggestion.pipeline,
    suggestion_id: suggestion.id,
    service_id: serviceID,
    selection_source: selectionSource,
    ...(batchCount != null ? { batch_count: batchCount } : {}),
  });
}

export function captureKitCategoryToggled({
  capture,
  pipeline,
  action,
}: {
  capture?: PosthogCapture;
  pipeline: SuggestionPipelines;
  action: KitCategoryToggleAction;
}): void {
  capture?.(KitPosthogEvents.CategoryToggled, {
    pipeline,
    action,
  });
}

type CaptureKitInfoHoverArgs = {
  capture?: PosthogCapture;
  infoTarget: KitInfoHoverTarget;
  suggestion?: GenericSuggestion;
  serviceID?: Undef<number | string>;
};

export function captureKitInfoHover({
  capture,
  infoTarget,
  suggestion,
  serviceID,
}: CaptureKitInfoHoverArgs): void {
  capture?.(KitPosthogEvents.InfoHover, {
    info_target: infoTarget,
    ...(suggestion?.pipeline
      ? {
          pipeline: suggestion.pipeline,
          suggestion_id: suggestion.id,
        }
      : {}),
    ...(serviceID != null ? { service_id: serviceID } : {}),
  });
}

// Radix Tooltip `onOpenChange` helper — only records when the tooltip opens.
export function captureKitInfoHoverOnOpen(
  open: boolean,
  args: CaptureKitInfoHoverArgs
): void {
  if (!open) {
    return;
  }
  captureKitInfoHover(args);
}

export function captureKitSuggestionPreviewOpened({
  capture,
  suggestion,
  serviceID,
}: {
  capture?: PosthogCapture;
  suggestion: GenericSuggestion;
  serviceID: Undef<number | string>;
}): void {
  if (!suggestion.pipeline) {
    return;
  }
  captureKitInfoHover({
    capture,
    infoTarget: KitInfoHoverTarget.SuggestionPreview,
    suggestion,
    serviceID,
  });
}

export function captureKitSuggestionsLoadMore({
  capture,
  pipeline,
  visibleCount,
  totalCount,
}: {
  capture?: PosthogCapture;
  pipeline: SuggestionPipelines;
  visibleCount: number;
  totalCount: number;
}): void {
  capture?.(KitPosthogEvents.SuggestionsLoadMore, {
    pipeline,
    visible_count: visibleCount,
    total_count: totalCount,
  });
}

export function captureKitHandledQuoteSelectionChanged({
  capture,
  laneId,
  selected,
  selectionCount,
}: {
  capture?: PosthogCapture;
  laneId: number;
  selected: boolean;
  selectionCount: number;
}): void {
  capture?.(KitPosthogEvents.HandledQuoteSelectionChanged, {
    lane_id: laneId,
    selected,
    selection_count: selectionCount,
  });
}

export function captureKitHandledQuoteBulkSelectionChanged({
  capture,
  action,
  laneCount,
}: {
  capture?: PosthogCapture;
  action: KitHandledQuoteBulkSelectionAction;
  laneCount: number;
}): void {
  capture?.(KitPosthogEvents.HandledQuoteBulkSelectionChanged, {
    action,
    lane_count: laneCount,
  });
}

export function captureKitHandledQuoteReset({
  capture,
  quoteCount,
}: {
  capture?: PosthogCapture;
  quoteCount: number;
}): void {
  capture?.(KitPosthogEvents.HandledQuoteReset, {
    quote_count: quoteCount,
  });
}

export function captureKitHandledQuoteViewToggled({
  capture,
  view,
}: {
  capture?: PosthogCapture;
  view: KitHandledQuoteView;
}): void {
  capture?.(KitPosthogEvents.HandledQuoteViewToggled, { view });
}

export function captureKitViewPulseShown({
  capture,
  targetView,
  threadId,
}: {
  capture?: PosthogCapture;
  targetView: KitPulseTargetView;
  threadId: Maybe<string>;
}): void {
  capture?.(KitPosthogEvents.ViewPulseShown, {
    target_view: targetView,
    thread_id: threadId || undefined,
  });
}

export function getKitViewPulseKey(
  threadId: Maybe<string>,
  targetView: KitPulseTargetView
): string {
  return `${threadId ?? ''}:${targetView}`;
}

// Props for Button `buttonNamePosthog={KitPosthogEvents.ThreadCheckRequested}`.
export function getKitThreadCheckRequestedLogProperties({
  kind,
  threadId,
  emailId,
  isRetry,
}: {
  kind: KitThreadCheckKind;
  threadId: Maybe<string>;
  emailId: Undef<Maybe<number>>;
  isRetry: boolean;
}): Record<string, unknown> {
  return {
    kind,
    thread_id: threadId || undefined,
    email_id: emailId ?? undefined,
    is_retry: isRetry,
  };
}

export function captureKitThreadCheckCompleted({
  capture,
  job,
}: {
  capture?: PosthogCapture;
  job: KitThreadCheckJob;
}): void {
  capture?.(KitPosthogEvents.ThreadCheckCompleted, {
    kind: job.kind,
    job_id: job.jobId,
    thread_id: job.threadId,
    status: job.status,
    emails_checked: job.result?.emailsChecked ?? job.progress.completed,
    new_suggestion_count: job.result?.newSuggestionCount ?? 0,
  });
}

export function captureKitThreadCheckFeedback({
  capture,
  job,
  helpful,
  hasComment,
}: {
  capture?: PosthogCapture;
  job: KitThreadCheckJob;
  helpful: boolean;
  hasComment: boolean;
}): void {
  capture?.(KitPosthogEvents.ThreadCheckFeedback, {
    kind: job.kind,
    job_id: job.jobId,
    thread_id: job.threadId,
    helpful,
    has_comment: hasComment,
    new_suggestion_count: job.result?.newSuggestionCount ?? 0,
  });
}
